import { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Search, Library, Loader2, Plus, Pencil, Trash2, X, Save,
  Shield, Link2, Layers,
} from 'lucide-react';
import MappingsManagerDialog from '../components/MappingsManagerDialog';

const ROUTE_TO_FRAMEWORK = {
  iso27001: 'ISO27001:2022',
  soc2: 'SOC2',
  essential8: 'EssentialEight',
};

function RequirementEditDialog({ open, onOpenChange, requirement, frameworkId, onSaved, canWrite }) {
  const [externalCode, setExternalCode] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setExternalCode(requirement?.external_code || '');
      setTitle(requirement?.title || '');
      setDescription(requirement?.description || '');
      setCategory(requirement?.category || '');
    }
  }, [open, requirement]);

  const isCreate = !requirement;

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {
        external_code: externalCode.trim(),
        title: title.trim() || null,
        description: description.trim() || null,
        category: category.trim() || null,
      };
      let res;
      if (isCreate) {
        res = await axios.post(`${API}/frameworks-v2/${encodeURIComponent(frameworkId)}/requirements`, body);
      } else {
        res = await axios.patch(
          `${API}/frameworks-v2/${encodeURIComponent(frameworkId)}/requirements/${requirement.id}`,
          body,
        );
      }
      toast.success(isCreate ? 'Requirement added' : 'Requirement updated');
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save requirement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="requirement-edit-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isCreate ? 'Add framework requirement' : 'Edit framework requirement'}</DialogTitle>
          <DialogDescription>
            Codes stay as the identifier. Titles/descriptions should only be filled from authoritative framework reference data.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <label className="text-xs font-semibold uppercase text-slate-600">External code</label>
            <Input
              data-testid="req-edit-code"
              value={externalCode}
              onChange={(e) => setExternalCode(e.target.value)}
              placeholder="e.g. A.5.17"
              disabled={!canWrite}
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase text-slate-600">Category</label>
            <Input
              data-testid="req-edit-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="e.g. Organizational controls"
              disabled={!canWrite}
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase text-slate-600">
              Official title <span className="text-slate-400 font-normal">(leave blank until authoritative)</span>
            </label>
            <Input
              data-testid="req-edit-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Populate from official framework reference only"
              disabled={!canWrite}
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase text-slate-600">Description</label>
            <Textarea
              data-testid="req-edit-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              disabled={!canWrite}
              className="mt-1"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="req-edit-cancel">Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !canWrite || !externalCode.trim()} data-testid="req-edit-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RequirementDetailDrawer({ open, onOpenChange, requirement, frameworkId, canWrite, onChanged }) {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [mappingsOpen, setMappingsOpen] = useState(false);

  const load = useCallback(async () => {
    if (!requirement) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/frameworks-v2/${encodeURIComponent(frameworkId)}/requirements/${requirement.id}`,
      );
      setDetail(res.data);
    } catch {
      toast.error('Failed to load requirement');
    } finally {
      setLoading(false);
    }
  }, [requirement, frameworkId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const removeMapping = async (mappingId) => {
    if (!window.confirm('Remove this mapping? The master control will remain in the library.')) return;
    try {
      await axios.delete(`${API}/framework-mappings/${mappingId}`);
      toast.success('Mapping removed');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove mapping');
    }
  };

  if (!requirement) return null;
  const req = detail?.requirement || requirement;
  const controls = detail?.mapped_controls || [];
  const compliance = detail?.compliance;

  const STATUS_COLORS = {
    'Not Assessed': 'bg-slate-100 text-slate-700 border-slate-200',
    'Non Compliant': 'bg-red-100 text-red-800 border-red-200',
    'Partially Compliant': 'bg-blue-100 text-blue-800 border-blue-200',
    'Compliant': 'bg-emerald-100 text-emerald-800 border-emerald-200',
    'Not Applicable': 'bg-slate-50 text-slate-500 border-slate-200',
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="requirement-detail-dialog" className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span className="font-mono text-slate-500">{req.external_code}</span>
              <span>{req.title || <em className="text-slate-400 font-normal">Official title not set</em>}</span>
            </DialogTitle>
            <DialogDescription>
              <span className="inline-flex items-center gap-2">
                {req.category && <span className="inline-flex items-center rounded-md border border-slate-300 bg-slate-50 px-2 py-0.5 text-xs text-slate-700">{req.category}</span>}
                {req.description && <span className="text-slate-600">{req.description}</span>}
              </span>
            </DialogDescription>
          </DialogHeader>

          {compliance && controls.length > 0 && (
            <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-center justify-between text-sm">
              <div className="flex items-center gap-4">
                <div>
                  <div className="text-xs text-slate-500">Derived compliance</div>
                  <div className="text-lg font-bold text-slate-900" data-testid="req-derived-compliance">
                    {compliance.percentage == null ? 'N/A' : `${compliance.percentage}%`}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Rollup status</div>
                  <Badge className={`${STATUS_COLORS[compliance.status] || ''} border`} data-testid="req-derived-status">
                    {compliance.status}
                  </Badge>
                </div>
              </div>
              <div className="text-xs text-slate-500">
                From the assessments of the master controls below.
              </div>
            </div>
          )}

          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <Layers className="w-4 h-4 text-slate-500" />
                {controls.length} master control{controls.length === 1 ? '' : 's'} satisfy this requirement
              </div>
              {canWrite && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setMappingsOpen(true)}
                  data-testid="req-manage-mappings-btn"
                >
                  <Link2 className="w-4 h-4 mr-1" /> Manage mappings
                </Button>
              )}
            </div>
            {loading ? (
              <div className="py-10 text-center text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
              </div>
            ) : controls.length === 0 ? (
              <p className="text-sm text-slate-500 italic py-6 text-center">
                No master controls are mapped to this requirement yet.
              </p>
            ) : (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[90px]">ID</TableHead>
                      <TableHead>Master control</TableHead>
                      <TableHead>Assessment</TableHead>
                      <TableHead className="text-center w-[90px]">Evidence</TableHead>
                      <TableHead>Rationale</TableHead>
                      {canWrite && <TableHead className="w-[70px]"></TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {controls.map((c) => (
                      <TableRow key={c.control_id} data-testid={`req-mapped-row-${c.control_id}`}>
                        <TableCell className="font-mono text-slate-600">{c.control_id}</TableCell>
                        <TableCell>
                          <div className="font-medium text-slate-900">{c.name}</div>
                          <div className="text-xs text-slate-500 line-clamp-2">{c.description}</div>
                          <div className="text-[11px] text-slate-500 mt-1">{c.domain}</div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${STATUS_COLORS[c.assessment?.status] || ''} border text-xs`}>
                            {c.assessment?.status || 'Not Assessed'}
                          </Badge>
                          {c.assessment?.owner && (
                            <div className="text-[10px] text-slate-500 mt-1">Owner: {c.assessment.owner}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {c.assessment?.evidence_count > 0 ? (
                            <Badge className="bg-blue-50 text-blue-700 border-blue-200 border text-xs">
                              {c.assessment.evidence_count}
                            </Badge>
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-slate-600">
                          {c.rationale || <span className="text-slate-400 italic">—</span>}
                        </TableCell>
                        {canWrite && (
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeMapping(c.mapping_id)}
                              data-testid={`req-unmap-${c.control_id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {mappingsOpen && (
        <MappingsManagerDialog
          open={mappingsOpen}
          onOpenChange={setMappingsOpen}
          mode="requirement"
          requirement={req}
          onChanged={() => { load(); onChanged?.(); }}
        />
      )}
    </>
  );
}

export default function FrameworkRequirementsPage() {
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const canWrite = user?.role === 'admin' || user?.role === 'super_admin';
  // Pathname format: /frameworks/:key/requirements
  const parts = location.pathname.split('/').filter(Boolean);
  const routeKey = parts[1];
  const frameworkId = ROUTE_TO_FRAMEWORK[routeKey];

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editReq, setEditReq] = useState(null);

  const load = useCallback(async () => {
    if (!frameworkId) return;
    setLoading(true);
    try {
      const params = search.trim() ? `?search=${encodeURIComponent(search.trim())}` : '';
      const res = await axios.get(
        `${API}/frameworks-v2/${encodeURIComponent(frameworkId)}/requirements${params}`,
      );
      setData(res.data);
    } catch {
      toast.error('Failed to load requirements');
    } finally {
      setLoading(false);
    }
  }, [frameworkId, search]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    if (!data?.requirements) return [];
    const map = {};
    data.requirements.forEach((r) => {
      const cat = r.category || 'Uncategorized';
      (map[cat] ||= []).push(r);
    });
    return Object.entries(map).map(([category, reqs]) => ({ category, reqs }));
  }, [data]);

  const deleteRequirement = async (req) => {
    if (!window.confirm(`Delete requirement ${req.external_code}? This requires zero existing mappings.`)) return;
    try {
      await axios.delete(`${API}/frameworks-v2/${encodeURIComponent(frameworkId)}/requirements/${req.id}`);
      toast.success('Requirement deleted');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete requirement');
    }
  };

  if (!frameworkId) {
    return <div className="p-8 text-slate-600">Unknown framework: {routeKey}</div>;
  }

  const fw = data?.framework;

  return (
    <div className="space-y-6" data-testid="framework-requirements-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Library className="w-4 h-4" />
            <span>Compliance Engine v2</span>
            <span className="text-slate-300">/</span>
            <span>{fw?.short_name || frameworkId}</span>
            <span className="text-slate-300">/</span>
            <span>Requirements</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
            <Shield className="w-7 h-7" style={{ color: fw?.color || '#0f172a' }} />
            {fw?.name || frameworkId} Requirements
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Framework requirements are mapped many-to-many with master controls.
            Add, edit or remove mappings without duplicating controls.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Card className="border-slate-200">
            <CardContent className="py-3 px-4 flex items-center gap-3">
              <div className="text-2xl font-bold text-slate-900" data-testid="req-total-count">
                {data?.total || 0}
              </div>
              <div className="text-xs text-slate-500 leading-tight">Requirements</div>
            </CardContent>
          </Card>
          {canWrite && (
            <Button onClick={() => { setEditReq(null); setEditOpen(true); }} data-testid="add-requirement-btn">
              <Plus className="w-4 h-4 mr-1" /> Add requirement
            </Button>
          )}
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid="req-search-input"
              value={search}
              placeholder="Search by code, title, description, category…"
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-center py-16 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading requirements…
        </div>
      ) : grouped.length === 0 ? (
        <div className="text-center py-16 text-slate-500">No requirements match the current search.</div>
      ) : (
        <div className="space-y-4">
          {grouped.map(({ category, reqs }) => (
            <Card key={category} className="border-slate-200" data-testid={`req-category-${category}`}>
              <CardContent className="p-0">
                <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-sm font-semibold text-slate-700 flex items-center justify-between">
                  <span>{category}</span>
                  <span className="text-xs text-slate-500">{reqs.length} requirement{reqs.length === 1 ? '' : 's'}</span>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[130px]">Code</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead className="w-[160px] text-center">Mapped controls</TableHead>
                      {canWrite && <TableHead className="w-[120px]"></TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reqs.map((r) => (
                      <TableRow
                        key={r.id}
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => { setSelected(r); setDetailOpen(true); }}
                        data-testid={`req-row-${r.external_code}`}
                      >
                        <TableCell className="font-mono text-slate-700">{r.external_code}</TableCell>
                        <TableCell>
                          {r.title ? (
                            <span className="text-slate-900">{r.title}</span>
                          ) : (
                            <span className="text-slate-400 italic">Official title not set</span>
                          )}
                          {r.description && (
                            <div className="text-xs text-slate-500 line-clamp-1">{r.description}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge
                            variant="outline"
                            className={r.mapped_control_count > 0
                              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                              : 'bg-slate-50 border-slate-200 text-slate-500'}
                          >
                            {r.mapped_control_count}
                          </Badge>
                        </TableCell>
                        {canWrite && (
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost" size="sm"
                                onClick={() => { setEditReq(r); setEditOpen(true); }}
                                data-testid={`req-edit-${r.external_code}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost" size="sm"
                                onClick={() => deleteRequirement(r)}
                                data-testid={`req-delete-${r.external_code}`}
                                disabled={r.mapped_control_count > 0}
                                title={r.mapped_control_count > 0 ? 'Remove all mappings first' : 'Delete'}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <RequirementDetailDrawer
        open={detailOpen}
        onOpenChange={setDetailOpen}
        requirement={selected}
        frameworkId={frameworkId}
        canWrite={canWrite}
        onChanged={load}
      />
      <RequirementEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        requirement={editReq}
        frameworkId={frameworkId}
        canWrite={canWrite}
        onSaved={load}
      />
    </div>
  );
}
